/**
 * CookieConsent — GDPR/ePrivacy compliant cookie banner.
 *
 * - Fixed bottom bar, full-width on mobile / centered card on desktop
 * - Auto-accepts all after 30s countdown (resets on any interaction)
 * - Two choices: Essential Only / Accept All
 * - Google Consent Mode v2 integration
 * - Stores preference in localStorage (te_cookie_consent, 6-month TTL)
 * - Admin › Settings › Ads & Tracking: consent on by default, bar on/off, bar
 *   for EU / EEA / UK / CH visitors only (see src/lib/consent.ts)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  fetchConsentConfig, getEffectiveConsent, getStoredConsent, pushConsentEvent,
  saveConsent, shouldShowBanner, updateGoogleConsent,
} from "@/lib/consent";

const AUTO_ACCEPT  = 30; // seconds
const SHOW_DELAY   = 1200; // ms

export default function CookieConsent() {
  const [visible,   setVisible]   = useState(false);
  const [defaultOn, setDefaultOn] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_ACCEPT);
  const timerRef  = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stored or cached-default consent is applied at load by TrackingInit. Here the
  // fresh admin settings arrive: apply them if they changed what this visitor
  // gets, then show the bar if they are in its audience and haven't chosen.
  useEffect(() => {
    let cancelled = false;
    let showTimer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    const before = getEffectiveConsent();

    fetchConsentConfig().then((config) => {
      if (cancelled || getStoredConsent()) return;
      const now = getEffectiveConsent(config);
      if (!!now?.analytics !== !!before?.analytics || !!now?.marketing !== !!before?.marketing) {
        updateGoogleConsent(!!now?.analytics, !!now?.marketing);
        pushConsentEvent(!!now?.marketing);
      }
      if (!shouldShowBanner(config)) return;
      setDefaultOn(now?.source === "default");
      showTimer = setTimeout(() => {
        if (!getStoredConsent()) setVisible(true);
      }, Math.max(0, SHOW_DELAY - (Date.now() - startedAt)));
    });

    return () => {
      cancelled = true;
      if (showTimer) clearTimeout(showTimer);
    };
  }, []);

  // Countdown → auto-accept all after AUTO_ACCEPT seconds
  useEffect(() => {
    if (!visible) return;
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          handleAcceptAll("auto");
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Reset countdown on any user interaction with the banner
  const resetTimer = useCallback(() => {
    setCountdown(AUTO_ACCEPT);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) { handleAcceptAll("auto"); return 0; }
          return c - 1;
        });
      }, 1000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAcceptAll = (source: "visitor" | "auto" = "visitor") => {
    if (timerRef.current) clearInterval(timerRef.current);
    saveConsent(true, true, source);
    setVisible(false);
  };

  const handleEssentialOnly = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    saveConsent(false, false);
    setVisible(false);
  };

  // Progress: from 100% → 0% over AUTO_ACCEPT seconds
  const progress = (countdown / AUTO_ACCEPT) * 100;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 30 }}
          // Mobile: full-width flush to edges; desktop: centered card
          // bottom-20 on mobile to clear Tawk.to chat button
          className="fixed bottom-20 left-2 right-2 z-[9999] sm:bottom-4 sm:left-1/2 sm:-translate-x-1/2 sm:w-auto sm:max-w-xl sm:min-w-[420px]"
          onMouseEnter={resetTimer}
          onTouchStart={resetTimer}
        >
          <div className="rounded-2xl border border-border/60 bg-background/90 backdrop-blur-xl shadow-2xl overflow-hidden">
            {/* Countdown progress bar */}
            <div className="h-0.5 bg-muted w-full">
              <motion.div
                className="h-full bg-accent"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.9, ease: "linear" }}
              />
            </div>

            <div className="px-4 py-3">
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className="shrink-0 mt-0.5 w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                  <Cookie className="w-4 h-4 text-accent" />
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground leading-tight">
                    We use cookies
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    {defaultOn
                      ? "Analytics and advertising cookies are on so we can improve the site and measure our ads. Choose “Essential only” to switch them off. "
                      : "Essential cookies are always active. We also use analytics and advertising cookies to improve your experience and measure our ads. "}
                    <Link
                      to="/privacy"
                      className="underline underline-offset-2 hover:text-foreground transition-colors"
                    >
                      Privacy Policy
                    </Link>
                  </p>
                </div>
              </div>

              {/* Actions + countdown hint */}
              <div className="flex items-center gap-2 mt-3 ml-11">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEssentialOnly}
                  className="text-xs h-7 px-3 rounded-lg flex-1 sm:flex-none"
                >
                  Essential only
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleAcceptAll()}
                  className="text-xs h-7 px-3 rounded-lg flex-1 sm:flex-none bg-accent hover:bg-accent/90 text-accent-foreground"
                >
                  Accept all
                </Button>
                <span className="text-[10px] text-muted-foreground/60 ml-1 shrink-0 hidden sm:block">
                  Auto in {countdown}s
                </span>
              </div>

              {/* Mobile countdown hint */}
              <p className="text-[10px] text-muted-foreground/60 mt-1.5 ml-11 sm:hidden">
                Accepting all cookies in {countdown}s
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
